import type { Insight } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  DashFillIcon,
  IconButton,
  useToasterContext,
  SortableGridItem,
  useSortableGridContext,
} from "@tandemdiabetes/sugarmate-react-components"
import classNames from "classnames"
import { useCallback } from "react"
import type { MouseEventHandler } from "react"
import { useEditInsightModalContext } from "../EditInsightModal"
import { useInsightsContext } from "../InsightsContext"
import styles from "./Tile.module.scss"
import { TileChildren } from "./TileChildren"
import { useOverlayLoader } from "src/contexts"
import { useRemoveInsight } from "src/hooks"
import { RemoveInsightErrorToast } from "src/toasts"
import { injectProps } from "src/utils"

interface Props {
  id: string;
  insight: Insight;
}

export function SortableTile(props: Props) {
  const {
    id, insight,
  } = props

  const removeInsight = useRemoveInsight()
  const { openEditInsightModal } = useEditInsightModalContext()
  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()
  const { isEditing } = useInsightsContext()
  const { activeId } = useSortableGridContext()

  const deleteInsight: MouseEventHandler<HTMLButtonElement> = useCallback(
    () => {
      void (async () => {
        const ToastComponent = injectProps(
          RemoveInsightErrorToast,
          { insightKey: insight.key },
        )

        setIsOverlayLoaderVisible(true)

        try {
          if (!removeInsight) {
            throw new Error("cannot remove insight")
          }

          await removeInsight(insight)
        } catch {
          showToast(
            "remove-insight-error-toast",
            ToastComponent,
          )
        }

        setIsOverlayLoaderVisible(false)
      })()
    },
    [
      insight,
      removeInsight,
      showToast,
      setIsOverlayLoaderVisible,
    ],
  )

  const onClick = useCallback(
    () => {
      openEditInsightModal(insight)
    },
    [
      openEditInsightModal,
      insight,
    ],
  )

  const iconButtonClassName = classNames(
    styles.delete,
    { [styles.deleteHidden]: !isEditing || !!activeId },
  )

  return (
    <div
      className={styles.container}
      id="tile"
    >
      <IconButton
        onClick={deleteInsight}
        stylePreset="withoutBackground"
        className={iconButtonClassName}
        disabled={!isEditing || !removeInsight}
        id={`remove-button-${insight.key}`}
      >
        <DashFillIcon />
      </IconButton>
      <SortableGridItem
        id={id}
        className={styles.tile}
        onClick={onClick}
      >
        <TileChildren {...props} />
      </SortableGridItem>
    </div>
  )
}
